import { useEffect, useState } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Stack,
  HStack,
  Text,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { useRouter } from "next/router";
import { getRecruiterJobs, generateQuestionsFromJd } from "./api/recruiter";
import {
  saveQuestionGeneratorReturn,
  getQuestionGeneratorReturn,
  AI_QUESTION_GENERATOR_RETURN_KEY,
} from "../lib/questionGeneratorSession";

const QUESTION_FORMATS = [
  { format: "MCQ", label: "Multiple Choice (MCQ)" },
  { format: "MRI", label: "Multiple Response (MRI)" },
  { format: "Fill in the Blanks", label: "Fill in the Blanks" },
  { format: "Descriptive", label: "Descriptive" },
  { format: "Coding", label: "Coding" },
];

const DEFAULT_COUNTS = {
  MCQ: 2,
  MRI: 2,
  "Fill in the Blanks": 2,
  Descriptive: 2,
  Coding: 1,
};

const stripHtml = (value) => {
  if (!value) return "";
  return String(value)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const CreateQuestionsFromJdModal = ({
  isOpen,
  onClose,
  userId,
  companyId,
  jobId,
  jobRole,
  jobDescription: initialJobDescription,
}) => {
  const isJobLocked = Boolean(jobId);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [difficulty, setDifficulty] = useState("Medium");
  const [counts, setCounts] = useState(DEFAULT_COUNTS);
  const [loading, setLoading] = useState(false);

  const router = useRouter();
  const toast = useToast();

  useEffect(() => {
    if (!isOpen) return;

    const saved = getQuestionGeneratorReturn();
    if (saved?.restore && saved.modal === "createQuestionsFromJd" && saved.form) {
      const form = saved.form;
      setCounts({ ...DEFAULT_COUNTS, ...(form.counts || {}) });
      setDifficulty(form.difficulty || "Medium");
      setSelectedJobId(form.selectedJobId || (jobId ? String(jobId) : ""));
      setJobDescription(form.jobDescription || stripHtml(initialJobDescription || ""));
      sessionStorage.setItem(
        AI_QUESTION_GENERATOR_RETURN_KEY,
        JSON.stringify({ ...saved, restore: false })
      );
      return;
    }

    setCounts({ ...DEFAULT_COUNTS });
    setDifficulty("Medium");
    setSelectedJobId(jobId ? String(jobId) : "");
    setJobDescription(stripHtml(initialJobDescription || ""));
  }, [isOpen, jobId, initialJobDescription]);

  useEffect(() => {
    if (!isOpen || !companyId || isJobLocked) return;

    let isActive = true;
    const fetchJobs = async () => {
      setJobsLoading(true);
      try {
        const result = await getRecruiterJobs(companyId);
        if (!isActive) return;

        const jobInfo = Array.isArray(result?.job_info) ? result.job_info : [];
        const usableJobs = jobInfo.filter(
          (job) => job.status?.toLowerCase() !== "deprecated"
        );
        setJobs(usableJobs);
      } catch (error) {
        console.error("Error fetching recruiter jobs:", error);
        if (isActive) setJobs([]);
      } finally {
        if (isActive) setJobsLoading(false);
      }
    };

    fetchJobs();
    return () => {
      isActive = false;
    };
  }, [isOpen, companyId, isJobLocked]);

  // When opened from job settings, fetch JD if it wasn't passed from the card.
  useEffect(() => {
    if (!isOpen || !companyId || !isJobLocked || initialJobDescription) return;

    let isActive = true;
    const fetchCurrentJobDescription = async () => {
      try {
        const result = await getRecruiterJobs(companyId);
        if (!isActive) return;

        const jobInfo = Array.isArray(result?.job_info) ? result.job_info : [];
        const currentJob = jobInfo.find(
          (job) => String(job.job_id) === String(jobId)
        );
        if (currentJob?.job_description) {
          setJobDescription(stripHtml(currentJob.job_description));
        }
      } catch (error) {
        console.error("Error fetching job description:", error);
      }
    };

    fetchCurrentJobDescription();
    return () => {
      isActive = false;
    };
  }, [isOpen, companyId, isJobLocked, jobId, initialJobDescription]);

  const handleJobChange = (e) => {
    const nextJobId = e.target.value;
    setSelectedJobId(nextJobId);
    const job = jobs.find((j) => String(j.job_id) === String(nextJobId));
    if (job) {
      setJobDescription(stripHtml(job.job_description || ""));
    }
  };

  const handleCountChange = (format, value) => {
    const parsed = parseInt(value, 10);
    setCounts((prev) => ({
      ...prev,
      [format]: Number.isNaN(parsed) ? 0 : Math.max(0, parsed),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const plainJobDescription = stripHtml(jobDescription);
    if (!plainJobDescription) {
      toast({
        title: "Job description is required.",
        description: "Select a job or enter a job description.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const question_types = QUESTION_FORMATS.map(({ format }) => ({
      question_format: format,
      count: counts[format] || 0,
    })).filter((qt) => qt.count > 0);

    if (question_types.length === 0) {
      toast({
        title: "Add at least one question.",
        description: "Set a count greater than 0 for at least one format.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const payload = {
      company_id: Number(companyId),
      user_id: Number(userId),
      difficulty,
      job_description: plainJobDescription,
      question_types,
    };

    setLoading(true);
    try {
      const data = await generateQuestionsFromJd(payload);

      let normalized;
      if (data && Array.isArray(data.data)) {
        normalized = data;
      } else if (Array.isArray(data)) {
        normalized = { data };
      } else if (data && Array.isArray(data.questions)) {
        normalized = { data: data.questions };
      } else {
        normalized = { data: [] };
      }

      setLoading(false);
      onClose();

      const selectedJob = jobs.find(
        (j) => String(j.job_id) === String(selectedJobId)
      );
      const resolvedJobRole = isJobLocked
        ? jobRole
        : selectedJob?.job_role || jobRole || "";

      saveQuestionGeneratorReturn({
        path: router.asPath,
        modal: "createQuestionsFromJd",
        form: {
          selectedJobId,
          jobDescription,
          difficulty,
          counts,
        },
      });

      // Large JD payloads (esp. coding answers) exceed URL limits — use sessionStorage.
      if (typeof window !== "undefined") {
        sessionStorage.setItem(
          "aiGeneratedQuestionsData",
          JSON.stringify(normalized)
        );
      }

      router.push({
        pathname: "/AiGeneratedQuestions",
        query: {
          companyId,
          ...(resolvedJobRole ? { jobRole: resolvedJobRole } : {}),
        },
      });
    } catch (error) {
      console.error("Error generating questions from JD:", error);
      setLoading(false);
      toast({
        title: "Could not generate questions.",
        description:
          error.message || "An error occurred. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Question Generator with JD</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form onSubmit={handleSubmit}>
            {isJobLocked ? (
              <FormControl id="job-locked" mb={4}>
                <FormLabel>Job</FormLabel>
                <Input
                  value={
                    jobRole
                      ? `${jobRole} (#${jobId})`
                      : `Job #${jobId}`
                  }
                  isReadOnly
                  bg="gray.50"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Opened from the job you selected on the Jobs panel.
                </Text>
              </FormControl>
            ) : (
              <FormControl id="job-select" mb={4}>
                <FormLabel>
                  Select Job {jobsLoading && <Spinner size="xs" ml={2} />}
                </FormLabel>
                <Select
                  placeholder={
                    jobsLoading ? "Loading jobs..." : "Select a job"
                  }
                  value={selectedJobId}
                  onChange={handleJobChange}
                  isDisabled={jobsLoading}
                >
                  {jobs.map((job) => (
                    <option key={job.job_id} value={job.job_id}>
                      {job.job_role
                        ? `${job.job_role} (#${job.job_id})`
                        : `Job #${job.job_id}`}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl id="job-description" mb={4} isRequired>
              <FormLabel>Job Description</FormLabel>
              <Textarea
                placeholder="Job description from selected job"
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                rows={5}
              />
            </FormControl>

            <FormControl id="difficulty" mb={4} isRequired>
              <FormLabel>Difficulty</FormLabel>
              <Select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
              >
                <option value="Easy">Easy</option>
                <option value="Medium">Medium</option>
                <option value="Hard">Hard</option>
              </Select>
            </FormControl>

            <FormControl id="question-counts" mb={4}>
              <FormLabel>Number of Questions by Format</FormLabel>
              <Stack spacing={3}>
                {QUESTION_FORMATS.map(({ format, label }) => (
                  <HStack key={format} justify="space-between">
                    <Text flex="1">{label}</Text>
                    <Input
                      type="number"
                      min={0}
                      width="100px"
                      value={counts[format]}
                      onChange={(e) =>
                        handleCountChange(format, e.target.value)
                      }
                    />
                  </HStack>
                ))}
              </Stack>
            </FormControl>

            <ModalFooter px={0}>
              <Button variant="ghost" mr={3} onClick={onClose} isDisabled={loading}>
                Cancel
              </Button>
              <Button type="submit" colorScheme="green" isDisabled={loading}>
                {loading ? <Spinner size="sm" /> : "Submit"}
              </Button>
            </ModalFooter>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CreateQuestionsFromJdModal;
